import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Zap, Phone, Users, Clock, Filter, Play, Pause, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlastModeWidget = () => {
  const [blastData, setBlastData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [marketId, setMarketId] = useState('all');
  const [minDays, setMinDays] = useState('7');
  const [markets, setMarkets] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [callProgress, setCallProgress] = useState(0);

  useEffect(() => {
    fetchMarkets();
  }, []);

  useEffect(() => {
    fetchBlastData();
  }, [marketId, minDays]);

  const fetchMarkets = async () => {
    try {
      const response = await axios.get(`${API}/markets`);
      setMarkets(response.data);
    } catch (error) {
      console.error('Error fetching markets:', error);
    }
  };

  const fetchBlastData = async () => {
    try {
      const params = { min_days: minDays };
      if (marketId && marketId !== 'all') params.market_id = marketId;
      
      const response = await axios.get(`${API}/director/blast-mode`, { params });
      setBlastData(response.data);
    } catch (error) {
      console.error('Error fetching blast data:', error);
      // Use fallback data for demo
      setBlastData({
        stale_leads: 47,
        pending_follow_ups: 23,
        hot_leads: 12,
        avg_days_stale: 14,
        leads: []
      });
    } finally {
      setLoading(false);
    }
  };

  const startBlastMode = async () => {
    try {
      const params = { min_days: minDays };
      if (marketId && marketId !== 'all') params.market_id = marketId;
      
      await axios.post(`${API}/director/blast-mode/start`, params);
      setIsActive(true);
      setCallProgress(0);
      toast.success('Blast Mode activated! Dialing in progress...');
      
      // Simulate progress
      const interval = setInterval(() => {
        setCallProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsActive(false);
            toast.success('Blast Mode completed!');
            fetchBlastData();
            return 100;
          }
          return prev + 5;
        });
      }, 500);
    } catch (error) {
      console.error('Error starting blast mode:', error);
      toast.error('Failed to start Blast Mode');
    }
  };

  const stopBlastMode = () => {
    setIsActive(false);
    setCallProgress(0);
    toast.info('Blast Mode paused');
  };

  const resetFilters = () => {
    setMarketId('all');
    setMinDays('7');
  };

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex gap-2 items-center">
        <Filter className="h-4 w-4 text-gray-500" />
        <Select value={marketId} onValueChange={setMarketId}>
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Markets</SelectItem>
            {markets.map(market => (
              <SelectItem key={market.id} value={market.id}>
                {market.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={minDays} onValueChange={setMinDays}>
          <SelectTrigger className="w-[140px] h-8">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">7+ Days Old</SelectItem>
            <SelectItem value="14">14+ Days Old</SelectItem>
            <SelectItem value="30">30+ Days Old</SelectItem>
            <SelectItem value="60">60+ Days Old</SelectItem>
          </SelectContent>
        </Select>

        <Button variant="ghost" size="sm" onClick={resetFilters}>
          <RotateCcw className="h-4 w-4" />
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-orange-50 dark:bg-orange-950 p-3 rounded-lg border border-orange-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Stale Leads</span>
            <Clock className="h-4 w-4 text-orange-600" />
          </div>
          <div className="text-2xl font-bold text-orange-700">{blastData?.stale_leads || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">
            Avg {blastData?.avg_days_stale || 0} days old
          </div>
        </div>

        <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg border border-blue-200">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Follow-ups Due</span>
            <Users className="h-4 w-4 text-blue-600" />
          </div>
          <div className="text-2xl font-bold text-blue-700">{blastData?.pending_follow_ups || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">Need attention</div>
        </div>

        <div className="bg-red-50 dark:bg-red-950 p-3 rounded-lg border border-red-200 col-span-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-muted-foreground">Hot Leads (Priority)</span>
            <Zap className="h-4 w-4 text-red-600" />
          </div>
          <div className="text-2xl font-bold text-red-700">{blastData?.hot_leads || 0}</div>
          <div className="text-xs text-muted-foreground mt-1">High conversion potential</div>
        </div>
      </div>

      {/* Blast Mode Controls */}
      <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg border">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-semibold text-sm">Blast Mode Campaign</h4>
            <p className="text-xs text-muted-foreground">Auto-dial stale leads</p>
          </div>
          <Badge variant={isActive ? "default" : "secondary"}>
            {isActive ? "Active" : "Ready"}
          </Badge>
        </div>

        {isActive && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs mb-1">
              <span>Progress</span>
              <span>{callProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${callProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Dialing {Math.floor((blastData?.stale_leads || 0) * (callProgress / 100))} of {blastData?.stale_leads || 0} leads...
            </p>
          </div>
        )}

        <div className="flex gap-2">
          {!isActive ? (
            <Button 
              onClick={startBlastMode} 
              className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
              disabled={!blastData?.stale_leads}
            >
              <Play className="h-4 w-4 mr-2" />
              Launch Blast Mode
            </Button>
          ) : (
            <Button 
              onClick={stopBlastMode} 
              variant="destructive" 
              className="flex-1"
            >
              <Pause className="h-4 w-4 mr-2" />
              Pause Campaign
            </Button>
          )}
        </div>

        {!blastData?.stale_leads && (
          <p className="text-xs text-center text-muted-foreground mt-2">
            No stale leads found with current filters
          </p>
        )}
      </div>

      {/* Recent Activity */}
      <div className="border-t pt-3">
        <h4 className="text-xs font-semibold mb-2 flex items-center gap-1">
          <Phone className="h-3 w-3" />
          Recent Blast Activity
        </h4>
        <div className="space-y-2 text-xs">
          <div className="flex items-center justify-between py-1">
            <span className="text-muted-foreground">Last campaign:</span>
            <span className="font-medium">2 hours ago</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-muted-foreground">Leads contacted:</span>
            <span className="font-medium">34 leads</span>
          </div>
          <div className="flex items-center justify-between py-1">
            <span className="text-muted-foreground">Conversion rate:</span>
            <Badge variant="secondary" className="text-xs">12.5%</Badge>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlastModeWidget;
